/**
 * "Job at a glance" (job page v3 header, approved by Corbin 2026-10-09:
 * https://claude.ai/artifact/K5KvuVFK5DeKrwfcBAnPTg): the words and numbers.
 *
 * Pure functions over data the page already has (GET /api/jobs/:id and
 * GET /api/jobs/:id/finish-line). Nothing here decides anything new: a
 * device's tile comes from the calm Devices list's own rule (`buildDevices`),
 * the WO's "mapped" from the WO mapping card's rule (`woMappingVerified`),
 * and the paperwork line from the Finish Line's own promises.
 */
import { jobTypeWord } from "@/pages/dispatch/new-grid/card-state";
import type { FinishLine, JobPromise } from "@/lib/finish-line";
import { deviceCountMismatch, jobPageStatusLabel, tradeExpectsDevices } from "@/lib/vocabulary";
import { customerWoNumber } from "@/lib/wo-number";
import type { StageStrip } from "../jobDetailHelpers";
import { buildDevices } from "./calm/calmModel";
import type {
    JobAssetRow,
    JobDetail,
    JobRepairResult,
    JobTestResult,
    LinkedEstimate,
    LinkedInvoice,
    WorkOrderMappingInfo,
} from "./types";

// ── What ────────────────────────────────────────────────────────────────────

/** The big word: the job type in one word (the new Grid's rule), else "Job". */
export function glanceTypeWord(job: Pick<JobDetail, "job_type_code" | "job_type_name">): string {
    return jobTypeWord({ job_type_code: job.job_type_code ?? null, job_type_name: job.job_type_name ?? null }) ?? "Job";
}

/** The status chip's word. office_review reads "Closeout" (10/9). */
export function glanceStatusWord(status: string | null | undefined): string {
    return jobPageStatusLabel(status);
}

/** "WQ" from "William Quinones"; "?" when there's no name. */
export function initials(name: string | null | undefined): string {
    const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    const first = parts[0]!.charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : "";
    return `${first}${last}`.toUpperCase();
}

// ── Devices ─────────────────────────────────────────────────────────────────

/** Where a device tile (or a flag) takes you in the Devices list. */
export type DeviceFocus = "pass" | "fail" | "none" | "stray";

export interface GlanceFlag {
    kind: "title_count" | "no_devices" | "not_on_job" | "more_than_one";
    text: string;
    /** The fix: the Devices list (where the extra test row and the device rows are). */
    focus: DeviceFocus | null;
}

export interface GlanceDevices {
    onJob: number;
    passed: number;
    failed: number;
    /** On the job with no result (before the visit: still to test). */
    notTested: number;
    tested: number;
    /** Before the visit the third tile reads "To test". */
    notTestedWord: "Not tested" | "To test";
    flags: GlanceFlag[];
}

/**
 * The three tiles count DEVICES on the job, never reports (Corbin, 10/9).
 * Passed = passed, repaired or replaced as planned; Failed = failed or
 * incomplete; Not tested = everything else on the job. Reports that don't
 * reconcile with the devices get an orange flag rather than being hidden:
 *  - a test for a device that isn't on this job;
 *  - a device with more than one test on this job (one test per job + device);
 *  - the title's "(36)" against the devices attached (the old header chip);
 *  - no devices on a trade that expects them (the old "0 devices" chip).
 */
export function glanceDevices(
    job: Pick<JobDetail, "title" | "trade_name">,
    assets: JobAssetRow[],
    tests: JobTestResult[],
    repairs: JobRepairResult[],
    postVisit: boolean,
): GlanceDevices {
    const devices = buildDevices(assets, tests, repairs, postVisit);
    let passed = 0;
    let failed = 0;
    for (const d of devices) {
        if (d.tally === "passed" || d.tally === "repaired" || d.tally === "replaced") passed += 1;
        else if (d.tally === "failed" || d.tally === "incomplete") failed += 1;
    }
    const onJob = devices.length;
    const notTested = onJob - passed - failed;

    const flags: GlanceFlag[] = [];
    const mm = deviceCountMismatch(job.title, onJob);
    if (mm) flags.push({ kind: "title_count", text: `Title says ${mm.titleSays}, ${mm.attached} on job`, focus: null });
    if (onJob === 0 && tradeExpectsDevices(job.trade_name)) {
        flags.push({ kind: "no_devices", text: "No devices on this job · Add", focus: "none" });
    }
    // A bypass leg's result lives on its main's test, so a main that isn't
    // linked itself still counts as on the job.
    const onJobIds = new Set<string>();
    for (const a of assets) {
        onJobIds.add(a.asset_id);
        if (a.parent_asset_id) onJobIds.add(a.parent_asset_id);
    }
    const stray = tests.filter((t) => !onJobIds.has(t.asset_id));
    if (stray.length > 0) {
        const pass = stray.filter((t) => (t.overall_result ?? "").toLowerCase() === "pass").length;
        const what =
            pass === stray.length
                ? `passed test${stray.length === 1 ? " is" : "s are"}`
                : `test${stray.length === 1 ? " is" : "s are"}`;
        flags.push({
            kind: "not_on_job",
            text: `${stray.length} ${what} on a device not on this job · Fix`,
            focus: "stray",
        });
    }
    const perAsset = new Map<string, number>();
    for (const t of tests) if (onJobIds.has(t.asset_id)) perAsset.set(t.asset_id, (perAsset.get(t.asset_id) ?? 0) + 1);
    const doubled = [...perAsset.values()].filter((n) => n > 1).length;
    if (doubled > 0) {
        flags.push({
            kind: "more_than_one",
            text: `${doubled} device${doubled === 1 ? " has" : "s have"} more than one test · Fix`,
            focus: "pass",
        });
    }

    return {
        onJob,
        passed,
        failed,
        notTested,
        tested: passed + failed,
        notTestedWord: postVisit ? "Not tested" : "To test",
        flags,
    };
}

// ── Work order ──────────────────────────────────────────────────────────────

export type GlanceWorkOrder =
    | { kind: "none"; required: boolean }
    | {
          kind: "wo";
          number: string | null;
          template: string | null;
          /** The WO mapping card's rule: requirements confirmed AND fields > 0. */
          mapped: boolean;
          fields: number;
          hasTemplate: boolean;
          /** The mapper (null when the WO number came in without an intake). */
          mapperHref: string | null;
      };

export function glanceWorkOrder(
    job: Pick<JobDetail, "external_ref" | "briefing_wo_number" | "customer_requires_wo" | "kind">,
    wo: WorkOrderMappingInfo | null | undefined,
): GlanceWorkOrder {
    const number = customerWoNumber(job, { includePo: false });
    if (!wo && !number && job.kind !== "vendor_work_order") {
        return { kind: "none", required: !!job.customer_requires_wo };
    }
    const fields = wo?.mapped_field_count ?? 0;
    return {
        kind: "wo",
        number,
        template: wo?.template_name ?? null,
        mapped: !!wo && wo.requirements_confirmed === true && fields > 0,
        fields,
        hasTemplate: !!wo?.template_id,
        mapperHref: wo ? `/jobs/work-orders/review/${wo.intake_item_id}` : null,
    };
}

// ── Money ───────────────────────────────────────────────────────────────────

export interface GlanceMoneyRow {
    key: string;
    what: string;
    number: string | null;
    amount: string | null;
    state: string;
    /** Orange dot: something on us here (a quote never sent, a send that bounced). */
    act: boolean;
    href: string;
}

function dollars(v: number | string | null | undefined): string | null {
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export function invoiceState(inv: LinkedInvoice): { state: string; act: boolean } {
    const s = (inv.status ?? "").toLowerCase();
    if ((inv.undelivered_count ?? 0) > 0 && s !== "paid") return { state: "Email didn't arrive", act: true };
    if (s === "draft") return { state: "Draft", act: false };
    if (s === "void" || s === "voided") return { state: "Void", act: false };
    if (s === "paid") return { state: "Paid", act: false };
    if (s === "partially_paid") return { state: "Part paid", act: false };
    return (inv.emailed_count ?? 0) > 0 ? { state: "Sent, not paid", act: false } : { state: "Not sent", act: false };
}

export function estimateState(est: LinkedEstimate): { state: string; act: boolean } {
    const s = (est.status ?? "").toLowerCase();
    if (s === "draft") return { state: "Draft, not sent", act: true };
    if (s === "accepted" || s === "approved") return { state: "Approved", act: false };
    if (s === "declined") return { state: "Declined", act: false };
    if (s === "sent") return { state: "Sent", act: false };
    return { state: s.replace(/_/g, " ") || "—", act: false };
}

/** The estimate this job came from, the quotes it made, and its invoices (the old lineage chips). */
export function glanceMoney(
    job: Pick<
        JobDetail,
        | "source_estimate_id"
        | "source_estimate_display_number"
        | "source_estimate_status"
        | "source_estimate_approved_at"
    >,
    invoices: LinkedInvoice[],
    estimates: LinkedEstimate[],
): GlanceMoneyRow[] {
    const rows: GlanceMoneyRow[] = [];
    if (job.source_estimate_id) {
        rows.push({
            key: `from-${job.source_estimate_id}`,
            what: "From estimate",
            number: job.source_estimate_display_number ?? null,
            amount: null,
            state: job.source_estimate_approved_at ? "Approved" : (job.source_estimate_status ?? "").replace(/_/g, " "),
            act: false,
            href: `/commercial/${job.source_estimate_id}?type=estimate`,
        });
    }
    for (const e of estimates) {
        rows.push({
            key: e.id,
            what: "Quote",
            number: e.display_number ?? null,
            amount: dollars(e.subtotal),
            ...estimateState(e),
            href: `/commercial/${e.id}?type=estimate`,
        });
    }
    for (const inv of invoices) {
        rows.push({
            key: inv.id,
            what: "Invoice",
            number: inv.invoice_number ?? null,
            amount: dollars(inv.total_amount ?? inv.subtotal),
            ...invoiceState(inv),
            href: `/commercial/${inv.id}?type=invoice`,
        });
    }
    return rows;
}

// ── Paperwork ───────────────────────────────────────────────────────────────

/** The Finish Line promises that are paperwork, in the order they happen. */
const PAPER_IDS = ["work_order", "report", "filing", "packet_released", "packet_delivered"] as const;
const PAPER_SHORT: Record<(typeof PAPER_IDS)[number], string> = {
    work_order: "Completed WO",
    report: "Report",
    filing: "City",
    packet_released: "Packet",
    packet_delivered: "Delivered",
};

export interface GlancePaperwork {
    main: string;
    /** "Report ✓ · City: on us · Packet: blocked". */
    sub: string;
    /** Orange: the first open piece is on us. */
    act: boolean;
    done: boolean;
    /** Nothing is due yet (before the visit). */
    later?: boolean;
}

function shortState(p: JobPromise): string {
    switch (p.state) {
        case "kept":
            return "✓";
        case "blocked":
            return "waiting";
        case "waiting":
            return `with ${p.owner === "office" ? "us" : p.owner}`;
        case "later":
            return "after the visit";
        case "missed":
            return "missed";
        default:
            return "to do";
    }
}

export function glancePaperwork(fl: FinishLine | null): GlancePaperwork | null {
    if (!fl) return null;
    const ps = PAPER_IDS.map((id) => fl.promises.find((p) => p.id === id)).filter(
        (p): p is JobPromise => !!p && p.state !== "not_applicable",
    );
    if (ps.length === 0) return { main: "No paperwork for this job", sub: "", act: false, done: true };
    const first = ps.find((p) => p.state !== "kept");
    const sub = ps
        .map((p) => {
            const w = shortState(p);
            const name = PAPER_SHORT[p.id as (typeof PAPER_IDS)[number]];
            return w === "✓" ? `${name} ✓` : `${name}: ${w}`;
        })
        .join(" · ");
    if (!first) return { main: "All paperwork done", sub, act: false, done: true };
    // Before the visit every piece is "later": say so once, not five times.
    if (ps.every((p) => p.state === "later" || p.state === "kept")) {
        return {
            main: "Starts after the visit",
            sub: ps.map((p) => PAPER_SHORT[p.id as (typeof PAPER_IDS)[number]]).join(" · "),
            act: false,
            done: false,
            later: true,
        };
    }
    return {
        main: first.title,
        sub,
        act: first.owner === "office" && (first.state === "open" || first.state === "missed"),
        done: false,
    };
}

// ── Steps ───────────────────────────────────────────────────────────────────

export interface GlanceStep {
    key: StageStrip["stages"][number]["key"];
    label: string;
    when: string | null;
    state: "done" | "now" | "todo";
}

function shortDay(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The stage strip, as words with dates. "Close Out" reads "Closeout" (10/9). */
export function glanceSteps(strip: StageStrip): GlanceStep[] {
    return strip.stages.map((s) => ({
        key: s.key,
        label: s.key === "close_out" ? "Closeout" : s.label,
        when: s.state === "pending" ? null : shortDay(s.ts),
        state: s.state === "done" ? "done" : s.state === "current" ? "now" : "todo",
    }));
}

/** The phone's one line: "Step 5 of 8: Closeout · next Invoiced". */
export function stepLine(steps: GlanceStep[]): string | null {
    const i = steps.findIndex((s) => s.state === "now");
    if (i === -1) return steps.every((s) => s.state === "done") ? "Every step done" : null;
    const next = steps[i + 1];
    return `Step ${i + 1} of ${steps.length}: ${steps[i]!.label}${next ? ` · next ${next.label}` : ""}`;
}

// ── Who ─────────────────────────────────────────────────────────────────────

/** "Visited Thu, Sep 24 · 10:00 AM", "Booked …" or "Not booked yet". */
export function whenWords(
    job: Pick<JobDetail, "scheduled_start" | "arrived_at" | "en_route_at">,
    postVisit: boolean,
): string {
    const at = postVisit ? (job.arrived_at ?? job.scheduled_start ?? null) : (job.scheduled_start ?? null);
    if (!at) return postVisit ? "Visit date not recorded" : "Not booked yet";
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return "Not booked yet";
    const day = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${postVisit ? "Visited" : "Booked"} ${day} · ${time}`;
}
